<template>
	<div :class="$style['header-notifications']">
		<BaseIconButton
			v-testid="'notifications-bell'"
			data-notification-bell
			:disabled="!departmentId"
			:class="$style['header-notifications__bell']"
			@click="open"
		>
			<FontAwesomeIcon
				:icon="icons.faBell"
				size="lg"
			/>
			<small
				v-if="departmentId && notificationsCount"
				v-testid="'notifications-count'"
				:class="[
					$style['header-notifications__count'],
					notificationsOverflow && $style['header-notifications__count--dot']
				]"
			>
				{{ notificationsOverflow ? '' : notificationsCount }}
			</small>
		</BaseIconButton>
		<DropdownMenu
			ref="dropdown"
			test-id="notifications-dropdown"
			:class="$style['header-notifications__dropdown']"
			:handle-offset-click="false"
			@offset:click="close"
			@close="onDropdownClose"
		>
			<div :class="$style['header-notifications__dropdown-header']">
				<h4 :class="$style['header-notifications__dropdown-title']">
					{{ $t('notifications.title') }}
					<BaseIconButton
						:class="[
							$style['header-notifications__refresh-button'],
							(isRefreshing || $apollo.queries.notifications.loading) && $style['header-notifications__refresh-button--refreshing']
						]"
						@click="refresh"
					>
						<FontAwesomeIcon :icon="icons.faSync" />
					</BaseIconButton>
				</h4>
				<div :class="$style['header-notifications__dropdown-switch']">
					<FontAwesomeIcon
						:icon="enabledStatusIcon"
						:class="[
							$style['header-notifications__switch-icon'],
							isEnabled && $style['header-notifications__switch-icon--enabled']
						]"
					/>
					<BaseSwitch
						v-model="isEnabled"
						:class="$style['header-notifications__switch']"
					/>
				</div>
			</div>
			<Notifications
				ref="notifications"
				:notifications="computedNotifications"
				@success="handleDeleteNotification"
				@delete="deleteNotification"
			/>
			<!-- TODO: Add this link after query allNotifications created -->
			<!-- <RouterLink
        v-if="notifications.length > 0"
        :class="$style['header-notifications__link']"
        to="/"
      >
        View all notifications
      </RouterLink> -->
		</DropdownMenu>
	</div>
</template>
<script>
// import gql from 'graphql-tag'
import DropdownMenu from '@/components/Essentials/DropdownMenu'
import EditNotification from './EditNotification.gql'
import DeleteNotification from './DeleteNotification.gql'
import FetchNotifications from './FetchNotifications.gql'
import moment from '@/packages/moment'
import { Notifications } from '@/components/Layout/Notifications'
import { addToast, removeToast, clearToasts } from '@/components/Layout/Notifications/toasts'
import EventBus from '@/utils/event-bus'
import {  faSync } from '@fortawesome/pro-regular-svg-icons'
import { faBell, faBellOn, faBellSlash } from '@fortawesome/pro-light-svg-icons'
import { FETCH_PARTS } from '@/store/modules/Parts/action-types'
import { mapGetters } from '@/store/modules/Parts/helpers'
import { mapGetters as websocketGetters } from '@/store/modules/WebSocket/helpers'

const POLLING_INTERVAL = typeof window !== 'undefined' && window.innerWidth <= 768 ? 30000 : 10000 //ms
const LOCALSTORAGE_KEYS = ['notifications-enbled', null] // active, inactive key
const READ_NOTIFICATION_EXPIRATION_DAYS = 2

export default {
	name: 'AppHeaderNotifications',
	components: {
		DropdownMenu,
		Notifications
	},
	data () {
		return {
			icons: { faBell, faSync },
			isExpanded: false,
			isEnabled: JSON.parse(localStorage.getItem(LOCALSTORAGE_KEYS[0])) !== false,
			notifications: [],
			wsNotifications: [],
			pendingCreateFromDraftNotification: null,
			hasOpened: false,
			isRefreshing: false,
			readAtMap: {},
			suppressBacklogToasts: true
		}
	},
	computed: {

		...mapGetters(['picturesCallback']),
		...websocketGetters(['createFromDraftEvent', 'isCreateFromDraftModalOpen']),

		computedNotifications () {
			const isUnread = (notification) => notification.status !== 'read'
			return [
				...this.notifications,
				...this.wsNotifications
			].sort((a, b) =>
				(isUnread(b) - isUnread(a)) || (this.getNotificationDate(b) - this.getNotificationDate(a))
			)
		},

		departmentId () {
			return this.$auth().selectedDepartmentId
		},
		enabledStatusIcon () {
			return this.isEnabled ? faBellOn : faBellSlash
		},
		notificationsCount () {
			return this.computedNotifications.filter(notification => notification.status !== 'read').length
		},
		notificationsOverflow () {
			return this.notificationsCount > 9
		},
		configuration () {
			return this.$app().configuration()
		}
	},
	watch: {
		departmentId (value) {
			if (!value) {
				this.$apollo.queries.notifications.stopPolling()
			}
		},

		picturesCallback: {
			immediate: false,
			handler (newVal, oldVal) {
				if (newVal && newVal?.timestamp !== oldVal?.timestamp && newVal?.partId) {
					this.onWebsocketResult(newVal)
				}

			}
		},
		createFromDraftEvent: {
			immediate: false,
			handler (newVal, oldVal) {
				if (newVal && newVal?.timestamp !== oldVal?.timestamp) {
					this.onWebsocketResult(newVal)
				}
			}
		},

		isCreateFromDraftModalOpen (isOpen, wasOpen) {
			if (!isOpen && wasOpen && this.pendingCreateFromDraftNotification) {
				this.wsNotifications = [
					...this.wsNotifications,
					this.pendingCreateFromDraftNotification
				]
				this.showToast(this.pendingCreateFromDraftNotification)
				this.pendingCreateFromDraftNotification = null
			}
		},

		notifications (newList) {
			const suppressToasts = this.suppressBacklogToasts
			this.suppressBacklogToasts = false
			if (!this.isEnabled) return

			const undelivered = newList.filter(notification =>
				!notification.ws &&
				notification.receiverType === 'user' &&
				!this.isDelivered(notification) &&
				!this.toastedIds.has(notification.id)
			)
			if (!undelivered.length) return

			undelivered.forEach(notification => {
				this.toastedIds.add(notification.id)
				if (!suppressToasts) this.showToast(notification)
				this.editNotification({
					id: notification.id,
					notificationType: notification.type,
					receiverType: notification.receiverType,
					resourceId: this.$auth().id,
					newData: {
						notificationStatus: 'delivered'
					}
				})
			})

			const hasBulkUpdate = undelivered.some(notification => notification.type === 'bulk_update_operation')
			if (hasBulkUpdate && this.$route.name === 'Dashboard:Parts') {
				// refetch parts
				this.handleRefetchParts()
			}
		},
		isEnabled (value) {
			if (!value) {
				this.$apollo.queries.notifications.stopPolling()
				clearToasts()
			} else {
				this.suppressBacklogToasts = true
				this.$apollo.queries.notifications.startPolling(POLLING_INTERVAL)
			}
			localStorage.setItem(LOCALSTORAGE_KEYS[0], value)
		}
	},
	apollo: {
		notifications: {
			query: FetchNotifications,
			skip () {
				return !this.departmentId || !this.isEnabled
			},
			variables () {
				return {
					departmentId: this.departmentId
				}
			},
			result () {
				if (this.isEnabled) {
					this.$apollo.queries.notifications.startPolling(POLLING_INTERVAL)
				} else {
					this.$apollo.queries.notifications.stopPolling()
				}
			}
		}
	},
	created () {
		this.toastedIds = new Set()
		EventBus.$on('notification-toast:action', this.onToastAction)
	},
	beforeDestroy () {
		EventBus.$off('notification-toast:action', this.onToastAction)
	},
	methods: {

		onWebsocketResult (event) {
			if (!event) return
			if (event.results || event.__typename === 'ErrorEvent' || event.type === 'CREATE_GROUPED_SALE_PARTS_FROM_DRAFT') {
				this.handleWebsocketResult({
					...event
				})
			}
		},

		handleWebsocketResult (event) {
			if ([
				'CONFIRM_PART_PICTURES_UPLOAD'
			].includes(event.type)) {
				const notification = {
					...event,
					...event.results,
					id: event.requestId,
					status: 'new',
					receiverType: 'user',
					resourceId: this.$auth().id,
					ws: true,
					type: 'CONFIRM_PART_PICTURES_UPLOAD'
				}
				this.notifications = [
					...this.notifications,
					notification
				]
				this.showToast(notification)
			}

			if ([
				'CREATE_GROUPED_SALE_PARTS_FROM_DRAFT'
			].includes(event.type)) {
				const results = event.results || {}
				const isFinished = event.__typename === 'ErrorEvent' || ['completed', 'failed'].includes(results?.state)
				if (!isFinished) return

				const notification = {
					...event,
					...results,
					id: event.requestId,
					status: 'new',
					ws: true,
					type: 'CREATE_GROUPED_SALE_PARTS_FROM_DRAFT'
				}

				if (this.isCreateFromDraftModalOpen) {
					this.pendingCreateFromDraftNotification = notification
					return
				}

				this.wsNotifications = [
					...this.wsNotifications,
					notification
				]
				this.showToast(notification)
			}
		},

		getNotificationDate (notification) {
			const date = notification.date || notification.timestamp
			return date ? new Date(date).getTime() : 0
		},

		isDelivered (notification) {
			// status check covers notifications marked delivered before deliveredAt existed
			return Boolean(notification.deliveredAt) || notification.status !== 'new'
		},

		showToast (notification) {
			if (!this.isEnabled) return
			addToast(notification)
		},

		onToastAction (notification) {
			this.markAsRead(notification)
		},

		open () {
			if (this.hasOpened) {
				this.deleteOldReadNotifications()
			}
			this.$refs.dropdown.open()
		},
		close () {
			this.$refs.dropdown.close()
		},

		onDropdownClose () {
			this.markAllAsRead()
			this.hasOpened = true
		},
		editNotification (payload) {
			return this.$apollo.mutate({
				mutation: EditNotification,
				variables: {
					payload
				}
			}).catch(error => {
				this.$store.dispatch('Notification/ADD_ERROR_NOTIFICATION', error)
			})
		},
		async refresh () {
			if (this.isEnabled) {
				this.$apollo.queries.notifications.refetch()
				return
			}
			// query is skipped while notifications are disabled, so fetch once manually
			this.isRefreshing = true
			try {
				const { data } = await this.$apollo.query({
					query: FetchNotifications,
					variables: {
						departmentId: this.departmentId
					},
					fetchPolicy: 'network-only'
				})
				this.notifications = data.notifications
			} catch (error) {
				this.$store.dispatch('Notification/ADD_ERROR_NOTIFICATION', error)
			} finally {
				this.isRefreshing = false
			}
		},

		async handleRefetchParts () {
			this.$store.dispatch(`Parts/${FETCH_PARTS}`, { page: this.configuration.page, size: this.configuration.perPage })
		},

		getResourceId (notification) {
			if (notification.receiverType === 'user') {
				return this.$auth().id
			}
			return notification.departmentId || this.departmentId
		},

		markAsRead (notification) {
			const now = new Date().toISOString()
			this.$set(this.readAtMap, notification.id, now)
			removeToast(notification)
			this.wsNotifications = this.wsNotifications.map(_notification =>
				_notification.id === notification.id ? { ..._notification, status: 'read' } : _notification
			)
			this.notifications = this.notifications.map(_notification =>
				_notification.id === notification.id ? { ..._notification, status: 'read' } : _notification
			)
			if (notification.ws) return
			const payload = {
				id: notification.id,
				notificationType: notification.type,
				receiverType: notification.receiverType,
				resourceId: this.getResourceId(notification),
				newData: {
					notificationStatus: 'read'
				}
			}
			this.editNotification(payload)
		},

		markAllAsRead () {
			this.computedNotifications.forEach(notification => {
				if (notification.status !== 'read') {
					this.markAsRead(notification)
				}
			})
		},

		deleteNotification (notification) {
			if (notification.ws) {
				this.wsNotifications = this.wsNotifications.filter(_notification => _notification.id !== notification.id)
				this.notifications = this.notifications.filter(_notification => _notification.id !== notification.id)
				return
			}
			const payload = {
				id: notification.id,
				notificationType: notification.type,
				receiverType: notification.receiverType,
				resourceId: this.getResourceId(notification)
			}
			this.$apollo.mutate({
				mutation: DeleteNotification,
				variables: {
					payload
				}
			}).catch(error => {
				this.$store.dispatch('Notification/ADD_ERROR_NOTIFICATION', error)
			})
			this.notifications = this.notifications.filter(_notification => _notification.id !== notification.id)
			removeToast(notification)
		},

		deleteOldReadNotifications () {
			const cutoff = moment().subtract(READ_NOTIFICATION_EXPIRATION_DAYS, 'days')
			const isOld = (notification) => {
				const date = this.readAtMap[notification.id] || notification.readAt || notification.date || notification.timestamp
				if (!date) return false
				return moment(date).isBefore(cutoff)
			}
			const isPersistentNotification = (notification) =>
				notification.__typename === 'EbayTokenInvalidNotification' || notification.type === 'ebay_token_invalid'
			this.computedNotifications.forEach(notification => {
				if (notification.status === 'read' && isOld(notification) && !isPersistentNotification(notification)) {
					this.deleteNotification(notification)
				}
			})
		},

		handleDeleteNotification (notification) {
			this.deleteNotification(notification)
			this.close()
		},

		syncLocalStorageKeys () {
			const activeKey = LOCALSTORAGE_KEYS[0]
			const inActiveKey = LOCALSTORAGE_KEYS[1]
			if (inActiveKey) {
				const inActiveValue = localStorage.getItem(inActiveKey)
				if (inActiveValue) {
					localStorage.setItem(activeKey, inActiveValue)
					localStorage.removeItem(inActiveKey)
				}
			}
		}
	}
}
</script>
<style lang="scss" module>
$count-circle-size: 15px;
$count-dot-size: 4.5px;

.header-notifications {
    position: relative;
    display: flex;
    align-items: center;
    height: 100%;

    &__bell {
        position: relative;
    }

    &__count {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        top: 0;
        right: -5px;
        background-color: getColor(danger); // notification badge
        color: getColor(white);
        font-size: 10px;
        line-height: 1;
        font-weight: bold;
        width: $count-circle-size;
        height: $count-circle-size;
        border-radius: 100%;

        &--dot {
            &::after {
                content: '';
                background-color: getColor(surface);
                height: $count-dot-size;
                width: $count-dot-size;
                border-radius: 100%;
            }
        }
    }

    &__dropdown {
        z-index: 2;
        margin-top: 5px;
        right: -20px;
        top: 100%;
        transform-origin: top right;
        width: 500px;
        max-width: calc(100vw - 20px);
        border-radius: 5px;
    }

    &__dropdown-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px;
        border-bottom: 1px solid getColor(border-common);
        background-color: getColor(surface-elevated);
    }

    &__dropdown-title {
        display: flex;
        align-items: center;
        margin: 0;
        font-size: 16px;
        color: getColor(text);
    }

    &__refresh-button {
        margin-left: 8px;
        font-size: 12px;

        &--refreshing {
            animation: spin 0.3s infinite linear;
        }
    }

    &__dropdown-switch {
        display: flex;
        align-items: center;
    }

    &__switch-icon {
        margin-right: 15px;
        color: getColor(helper-text);

        &--enabled {
            color: getColor(text);
        }
    }

    &__link {
        display: flex;
        justify-content: center;
        width: 100%;
        text-decoration: none;
        padding: 15px;
        font-size: 14px;
        color: getColor(tertiary);
        border-top: 1px solid getColor(border-common);
        background-color: getColor(surface);

        &:hover {
            cursor: pointer;
            color: getColor(tertiary-hover);
            background-color: getColor(hover-overlay);
        }
    }
}

@keyframes spin {
    from {
        transform: rotate(0);
    }

    to {
        transform: rotate(359deg);
    }
}
</style>
